/**
 * The app's image component, implemented with expo-image. It keeps
 * react-native-fast-image's API and layout, which the app was written against:
 *
 * - `style` goes on a container View with `overflow: 'hidden'`, the image fills
 *   it, and `children` render on top of the image.
 * - `resizeMode` (default `cover`) maps to `contentFit`.
 * - Every image uses the memory and disk cache. fast-image's `immutable` did
 *   the same. Its `web` mode (iOS) also revalidated against HTTP cache headers,
 *   which expo-image can't do, so a file replaced at the same URL stays cached
 *   until it expires (a week on iOS) or is evicted.
 * - The view clears when the source changes, as fast-image's did, instead of
 *   showing the previous image until the new one loads (`recyclingKey`).
 * - A blank uri is an error on iOS and web and nothing on Android, as before
 *   (fast-image on iOS and Android, React Native Web's Image on web).
 */
import React, { forwardRef, memo, useEffect, useRef } from 'react';
import { Platform, StyleSheet, View, } from 'react-native';
import { Image } from 'expo-image';
const resizeMode = {
    contain: 'contain',
    cover: 'cover',
    stretch: 'stretch',
};
const cacheControl = {
    immutable: 'immutable',
    web: 'web',
};
const contentFit = {
    contain: 'contain',
    cover: 'cover',
    stretch: 'fill',
};
const toExpoSource = (source) => {
    if (source == null || typeof source === 'number')
        return source ?? null;
    const { uri, cache: _cache, priority: _priority, ...rest } = source;
    // A blank uri can't be converted to a URL on iOS. Without one, expo-image
    // fails the load on iOS (onError) and clears the image on Android, as
    // fast-image did. A uri that isn't a string, from untyped data, is treated
    // the same rather than throwing here.
    const valid = typeof uri === 'string' && uri.trim() !== '';
    return { ...rest, uri: valid ? uri : undefined };
};
const FastImageBase = forwardRef(({ source, resizeMode: mode = 'cover', style, children, onLoad, onError, ...props }, ref) => {
    const expoSource = toExpoSource(source);
    // React Native Web's Image failed a blank uri, and callers show a fallback
    // in onError. expo-image on web renders no image and no event instead.
    const blankUri = typeof source === 'object' &&
        typeof source?.uri === 'string' &&
        !source.uri.trim();
    // Callers often pass an inline onError, so it is read from a ref: as a
    // dependency it would fire again on every render. The ref is written in
    // an effect, not during render, and that effect runs first.
    const onErrorRef = useRef(onError);
    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);
    useEffect(() => {
        if (Platform.OS === 'web' && blankUri)
            onErrorRef.current?.();
    }, [blankUri]);
    return (<View ref={ref} style={[styles.imageContainer, style]}>
        <Image {...props} style={StyleSheet.absoluteFill} source={expoSource} recyclingKey={typeof expoSource === 'number'
            ? String(expoSource)
            : expoSource?.uri} contentFit={contentFit[mode]} cachePolicy="memory-disk" priority={typeof source === 'object' ? source?.priority : undefined} onLoad={onLoad ? () => onLoad() : undefined} onError={onError ? () => onError() : undefined}/>
        {children}
      </View>);
});
const styles = StyleSheet.create({
    imageContainer: {
        overflow: 'hidden',
    },
});
const FastImage = Object.assign(memo(FastImageBase), {
    resizeMode,
    cacheControl,
});
export default FastImage;
